import { __decorate } from "tslib";
import * as CONST from '../const';
import Reportable from '../decorators/reportable';
import DefaultReturn from '../decorators/defaultReturn';
import EventEmitter from 'events';
import CalculatorProjectGeometry from './geometry';
class CalculatorProject extends EventEmitter {
    calculator;
    image = CONST.DEFAULT_IMAGE;
    userImage = false;
    files = new Map();
    activeImageFileId = -1;
    width = CONST.DEFAULT_SIZE_WIDTH;
    height = CONST.DEFAULT_SIZE_HEIGHT;
    modulesVariant = JSON.parse(JSON.stringify(CONST.ONE_MODULE_PRINT));
    facesCount = 0;
    digital = CONST.DEFAULT_DIGITAL;
    properties = new Map();
    prices = new Map();
    total = 0.0;
    _geometry;
    constructor(data, calculator) {
        super();
        this.calculator = calculator;
        this.image = data.image;
        this.facesCount = data.facesCount ? data.facesCount : 0;
        this.width = data.width ? data.width : CONST.DEFAULT_SIZE_WIDTH;
        this.height = data.height ? data.height : CONST.DEFAULT_SIZE_HEIGHT;
        this.initProperties(data.properties);
        this.on('propertyChanged', (val) => this.checkIfImageUpdateNeeded(val));
        this._geometry = new CalculatorProjectGeometry(this);
    }
    get geometry() {
        return this._geometry;
    }
    initProperties(properties) {
        this.properties = new Map();
        for (const [key, val] of properties) {
            this.properties.set(key, val);
        }
        this.addRequiredProperties();
        this.updateTotal();
        this.updateSummary();
    }
    addRequiredProperties() {
        this.properties.set('wrapper', 7);
    }
    changeProperty(property, value) {
        this.runCommands(property, value);
        this.setProperty(property, value);
    }
    removeProperty(property) {
        this.runCommands(property, -1);
        this._removeProperty(property);
    }
    _removeProperty(property) {
        this.properties.delete(property);
        this.prices.delete(property);
        this.emit('propertyRemoved', {
            property
        });
        this.emit(`propertyRemoved.${property}`);
        this.updateSummary();
        this.updateTotal();
    }
    hideProperty(property, value) {
        this.calculator.hideProperty(property);
        this.emit('propertyHided', {
            property, value
        });
        this.emit(`propertyHided.${property}`);
    }
    showProperty(property) {
        const value = this.setPropertyToDefault(property);
        this.calculator.showProperty(property);
        this.emit('propertyShowed', {
            property,
            value
        });
        this.emit(`propertyShowed.${property}`);
    }
    setPropertyToDefault(property) {
        const value = this.getDefaultPropertyId(property);
        this.setProperty(property, value);
        return value;
    }
    getDefaultPropertyId(property) {
        return this.calculator.getDefaultProjectPropertyId(property);
    }
    runCommands(property, value) {
        const step = this.getPropertyStep(property);
        if (Object.prototype.hasOwnProperty.call(step, 'command')) {
            for (const com in step.command) {
                if ((com.indexOf('WhenOff') > -1 && value === -1)) {
                    const realCom = com.replace('WhenOff', '');
                    this.performCommand(realCom, step.command[com], 0);
                }
                else if (value !== -1) {
                    this.performCommand(com, step.command[com], value);
                }
            }
        }
        this.updateSummary();
        this.updateTotal();
    }
    setProperty(property, value) {
        this.properties.set(property, value);
        this.emit('propertyChanged', {
            property,
            value
        });
        this.emit(`propertyChanged.${property}`, {
            property,
            value
        });
        this.updateSummary();
        this.updateTotal();
    }
    performCommand(name, targets, value) {
        switch (name) {
            case 'remove':
                this.commandRemove(targets);
                break;
            case 'add':
                this.commandAdd(targets, 1);
                break;
            case 'hide':
                this.commandHide(targets, value);
                break;
            case 'show':
                this.commandShow(targets);
                break;
        }
    }
    commandRemove(targets) {
        targets.forEach((property) => {
            this._removeProperty(property);
        });
    }
    commandAdd(targets, value) {
        targets.forEach((property) => {
            this.emit('propertyAdded', {
                property,
                value
            });
            this.emit(`propertyAdded.${property}`);
            this.setProperty(property, value);
        });
    }
    commandHide(targets, value) {
        targets.forEach((property) => {
            this.hideProperty(property, value);
        });
    }
    commandShow(targets) {
        targets.forEach((property) => {
            this.showProperty(property);
        });
    }
    changePrice(property, value) {
        let old = undefined;
        if (this.prices.has(property)) {
            old = this.prices.get(property);
        }
        this.prices.set(property, value);
        if (old !== value) {
            this.emit(`price.changed.${property}`, { property, price: value });
        }
    }
    setPropertyPrice(property, value) {
        if (property === 'face' || this.properties.has(property)) {
            this.changePrice(property, value);
        }
        this.updateTotal();
    }
    getPropertyPrice(property) {
        const val = this.prices.get(property);
        if (typeof val === 'number') {
            return val;
        }
        else {
            return 0.0;
        }
    }
    updateTotal() {
        const old = this.total;
        this.total = Array.from(this.prices).reduce((acc, [, val]) => {
            acc += val;
            return acc;
        }, 0.0);
        if (old !== this.total) {
            this.emit('total.changed', this.total);
        }
        return this.total;
    }
    getPropertyValue(propery, id) {
        return this.calculator.getPropertyValue(propery, id);
    }
    getActivePropertyValue(property) {
        const id = this.getPropertyId(property);
        if (id > 0) {
            return this.getPropertyValue(property, id);
        }
        else {
            return undefined;
        }
    }
    changeImage(url) {
        this.image = url;
        this.emit('imageChanged', url);
        this.updateSummary();
    }
    getImage() {
        return this.image;
    }
    setImageFileId(id) {
        this.activeImageFileId = id;
    }
    getImageQuality() {
        const file = this.files.get(this.activeImageFileId);
        if (file) {
            const ratioFile = file.width / file.height;
            const ratioEditor = this.width / this.height;
            const ppi_w = file.width / (this.width / 2.54);
            const ppi_h = file.height / (this.height / 2.54);
            let ppi = 0;
            if (ratioFile > ratioEditor) {
                ppi = ppi_h;
            }
            else {
                ppi = ppi_w;
            }
            if (ppi < CONST.DESIRED_PPI) {
                return Math.floor((ppi / CONST.DESIRED_PPI) / 0.2);
            }
            else {
                return 5;
            }
        }
        return 0;
    }
    changeModulesFormat(variant) {
        console.log('changing modules format', variant);
        this.modulesVariant = Object.freeze(JSON.parse(JSON.stringify(variant)));
        this.emit('modulesFormatChanged', this.modulesVariant);
        this.updateSummary();
    }
    getModules() {
        return JSON.parse(JSON.stringify(this.modulesVariant.value));
    }
    getModulesVariantId() {
        return this.modulesVariant.formatModuleID;
    }
    getModulesVariant() {
        return JSON.parse(JSON.stringify(this.modulesVariant));
    }
    changeWidth(val) {
        this.width = val;
        this.emit('widthChanged', val);
        this.updateSummary();
    }
    changeHeight(val) {
        this.height = val;
        this.emit('heightChanged', val);
        this.updateSummary();
    }
    changeSize(val) {
        this.height = val.height;
        this.width = val.width;
        this.emit('sizeChanged', val);
        setTimeout(() => this.updateSummary(), 10);
    }
    changeFacesCount(val) {
        this.facesCount = val;
        this.emit('facesCountChanged', val);
        this.updateSummary();
    }
    setProject(data) {
        this.image = data.image;
        this.facesCount = data.facesCount ? data.facesCount : 0;
        this.width = data.width ? data.width : CONST.DEFAULT_SIZE_WIDTH;
        this.height = data.height ? data.height : CONST.DEFAULT_SIZE_HEIGHT;
        this.initProperties(data.properties);
    }
    getSize() {
        return {
            width: this.width,
            height: this.height
        };
    }
    getFacesCount() {
        return this.facesCount;
    }
    getPropertyStep(propery) {
        return this.calculator.getPropertyStep(propery);
    }
    isDigital() {
        return this.digital;
    }
    isMaterial() {
        return !this.isDigital();
    }
    setDigital() {
        this.digital = true;
        this.removeNotDigitalProperties();
        this.emit('digital');
        this.updateSummary();
    }
    setMaterial() {
        this.digital = false;
        this.emit('material');
        this.updateSummary();
    }
    isDesignStyled() {
        return this.properties.has('designStyle');
    }
    getDesignStyleValue() {
        const name = 'designStyle';
        return this.calculator.getPropertyValue(name, this.getPropertyId(name));
    }
    getPropertyId(property) {
        if (property === 'face') {
            return this.facesCount;
        }
        const res = this.properties.get(property);
        if (typeof res === 'number') {
            return res;
        }
        else {
            return -1;
        }
    }
    checkIfImageUpdateNeeded(val) {
        const { property } = val;
        if (property) {
            const newData = this.getActivePropertyValue(property);
            if (newData && typeof newData.stillImage !== 'undefined' && newData.stillImage) {
                this.updateStillImage(newData.stillImage);
            }
        }
    }
    updateStillImage(image) {
        if (!this.userImage) {
            this.changeImage(image);
        }
    }
    setUserImage() {
        this.userImage = true;
    }
    hasPaspartu() {
        return (this.properties.has('paspartu'));
    }
    removeNotDigitalProperties() {
        for (const [key,] of this.properties) {
            if (!CONST.DIGITAL_STEPS.includes(key)) {
                this.removeProperty(key);
            }
        }
    }
    isWithPaspartuOverflow() {
        return this.calculator.isWithPaspartuOverflow();
    }
    getSummary() {
        const result = {
            image: this.image,
            imageQuality: this.getImageQuality(),
            facesCount: this.facesCount,
            digital: this.digital,
            properties: {},
            modulesFormat: this.getModulesVariant(),
        };
        if (this.digital) {
            this.fillPropertiesForDigital(result);
        }
        else {
            this.fillPropertiesForMaterial(result);
        }
        for (const [key, value] of this.properties) {
            const price = this.prices.get(key);
            result.properties[key] = {
                value,
                price: typeof price === 'undefined' ? 0 : price
            };
        }
        return result;
    }
    fillPropertiesForDigital(result) {
    }
    fillPropertiesForMaterial(result) {
        result.width = this.width;
        result.height = this.height;
    }
    updateSummary() {
        const summary = this.getSummary();
        const summaryInString = this.stringifySummary(summary);
        this.emit('summary.changed', summaryInString);
    }
    stringifySummary(summary) {
        const result = [];
        /*
      if(typeof summary.image !== 'undefined'){
        if(summary.image.indexOf('data:image/')===-1){
          result.push(['Изображение', summary.image]);
        }else{
          if(this.activeImageFileId > 0 && this.files.has(this.activeImageFileId)){
            let file = this.files.get(this.activeImageFileId);
            if(file){
              result.push(['Изображение', `${file.old_name} -  ${file.url}`]);
            }
          }
        }
      }
      */
        if (typeof summary.facesCount !== 'undefined') {
            result.push(['Число лиц', summary.facesCount.toString()]);
        }
        if (typeof summary.digital !== 'undefined') {
            //result.push(['Тип', summary.digital?'Цифровой':'Материальный']);
        }
        if ((typeof summary.width !== 'undefined') &&
            (typeof summary.height !== 'undefined')) {
            result.push(['Размер', `${summary.width.toString()}X${summary.height.toString()}`]);
        }
        if ((typeof summary.imageQuality !== 'undefined')) {
            result.push(['Качество изображения', `${summary.imageQuality.toString()}`]);
        }
        if ((typeof summary.modulesFormat !== 'undefined') &&
            (typeof summary.modulesFormat.value !== 'undefined')) {
            result.push(['Номер схемы модулей', `${summary.modulesFormat.formatModuleID}`]);
            result.push(['Cхема модулей', JSON.stringify(summary.modulesFormat.value)]);
        }
        for (const key in summary.properties) {
            const pair = this.calculator.getPropertyStepAndValueStringified(key, summary.properties[key].value);
            if (pair.length === 2) {
                if (summary.properties[key].price > 0) {
                    result.push([...pair /*, `${summary.properties[key].price.toFixed(2)} р.`*/]);
                }
                else if (key === 'designStyle') {
                    result.push([...pair]);
                }
            }
        }
        result.push(["\n"]);
        return result.map(ar => ar.join(': ')).join("\n");
    }
    addFiles(files) {
        for (const file of files) {
            this.files.set(file.id, file);
        }
    }
    removeFiles(list) {
        for (const id of list) {
            this.files.delete(id);
        }
    }
    getFilesStringified() {
        return Array.from(this.files.values()).map(entr => entr.url).join("\n");
    }
    getTotal() {
        return this.total;
    }
    getBagetPrice() {
        return this.getPropertyPrice('baget');
    }
}
__decorate([
    Reportable
], CalculatorProject.prototype, "initProperties", null);
__decorate([
    Reportable
], CalculatorProject.prototype, "addRequiredProperties", null);
__decorate([
    Reportable
], CalculatorProject.prototype, "changeProperty", null);
__decorate([
    Reportable
], CalculatorProject.prototype, "removeProperty", null);
__decorate([
    Reportable
], CalculatorProject.prototype, "_removeProperty", null);
__decorate([
    Reportable
], CalculatorProject.prototype, "hideProperty", null);
__decorate([
    Reportable
], CalculatorProject.prototype, "showProperty", null);
__decorate([
    DefaultReturn(0.0),
    Reportable
], CalculatorProject.prototype, "updateTotal", null);
__decorate([
    DefaultReturn(false),
    Reportable
], CalculatorProject.prototype, "getPropertyValue", null);
__decorate([
    DefaultReturn(undefined),
    Reportable
], CalculatorProject.prototype, "getActivePropertyValue", null);
__decorate([
    Reportable
], CalculatorProject.prototype, "changeImage", null);
__decorate([
    Reportable
], CalculatorProject.prototype, "changeWidth", null);
__decorate([
    Reportable
], CalculatorProject.prototype, "changeHeight", null);
__decorate([
    Reportable
], CalculatorProject.prototype, "changeSize", null);
__decorate([
    Reportable
], CalculatorProject.prototype, "changeFacesCount", null);
__decorate([
    Reportable
], CalculatorProject.prototype, "setProject", null);
__decorate([
    DefaultReturn({ width: CONST.DEFAULT_SIZE_WIDTH, height: CONST.DEFAULT_SIZE_HEIGHT }),
    Reportable
], CalculatorProject.prototype, "getSize", null);
__decorate([
    DefaultReturn(0),
    Reportable
], CalculatorProject.prototype, "getFacesCount", null);
__decorate([
    DefaultReturn(false),
    Reportable
], CalculatorProject.prototype, "getPropertyStep", null);
__decorate([
    Reportable
], CalculatorProject.prototype, "getDesignStyleValue", null);
__decorate([
    Reportable
], CalculatorProject.prototype, "getPropertyId", null);
export default CalculatorProject;
